import { useState } from 'react';
import { SelectOption } from '../searchableSelect/searchableSelectTypes';
import { getTranslationFromTranslatableEntity } from '../../constants/matter';
import { useLanguage } from '../../context/LanguageContext';
import { useManageMatterRequestContext } from '../../context/ManageMatterRequest';
import { useFetchCountries } from '../../hooks/useFetchCountries';
import { ICountry } from '../../interface/countries';
import { IMatterRequest } from '../../interface/matterRequest';

/** The props `SearchableSelect` needs to pick the home country of a new customer, from the same list the picker screen uses. */
export const useMatterRequestHomeCountrySelect = () => {
  const { matterRequest, updateManageMatterRequest } = useManageMatterRequestContext();
  const { language: appLanguage } = useLanguage();
  const [searchValue, setSearchValue] = useState('');
  const {
    data: countries,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useFetchCountries({ pageSize: 100, search: searchValue });

  const options: SelectOption[] = countries
    .map((country: ICountry) => ({
      value: country.countryCode,
      label: getTranslationFromTranslatableEntity(country.translations, appLanguage),
    }))
    .sort((a: SelectOption, b: SelectOption) => a.label.localeCompare(b.label, appLanguage));

  const homeCountry = matterRequest?.newCustomer?.homeCountry;
  const value: SelectOption | null = homeCountry
    ? {
        value: homeCountry,
        label: matterRequest?.newCustomer?.homeCountryTranslatedName ?? homeCountry,
      }
    : null;

  const onChange = (option: SelectOption) => {
    const country = countries.find(({ countryCode }: ICountry) => countryCode === option.value);
    if (!country) return;
    updateManageMatterRequest({
      ...matterRequest,
      newCustomer: {
        ...matterRequest?.newCustomer,
        homeCountry: country.countryCode,
        homeCountryTranslatedName: getTranslationFromTranslatableEntity(
          country.translations,
          appLanguage,
        ),
      },
    } as unknown as IMatterRequest);
  };

  return {
    options,
    value,
    onChange,
    onSearch: setSearchValue,
    onLoadMore: fetchNextPage,
    hasMore: Boolean(hasNextPage),
    loading: isLoading,
    loadingMore: isFetchingNextPage,
  };
};
